import { useState, useEffect } from 'react';
import { BASE, authFetch } from '../constants';

const REFLECTIONS_API = `${BASE}/api/v1/reflections`;
const LEARNINGS_API = `${BASE}/api/v1/learnings`;

// ── Color maps ──
const PRIORITY_COLORS: Record<string, string> = { critical: '#EF4444', high: '#F97316', medium: '#EAB308', low: '#6B7280' };
const STATUS_COLORS: Record<string, string> = { proposed: 'var(--navy-700)', approved: '#22C55E', rejected: '#EF4444', active: 'var(--accent)', reverted: '#6B7280', confirmed: '#22C55E', archived: '#6B7280' };
const CATEGORY_COLORS: Record<string, string> = { decision_precedent: 'var(--navy-700)', entity_pattern: '#22C55E', user_preference: '#A855F7', policy_interpretation: '#F97316', performance: 'var(--navy-700)', pattern: '#22C55E', calibration: '#EAB308', recurring_issue: '#EF4444' };

// ── Helpers ──
function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${color}20`, color, whiteSpace: 'nowrap' }}>
      {label.replace(/_/g, ' ')}
    </span>
  );
}

function timeAgo(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const thStyle: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' };
const tdStyle: React.CSSProperties = { padding: '8px 10px', fontSize: 12, color: 'var(--text-secondary)' };

// ── Types ──
interface Reflection { id: number; agent_code: string; reflection_type: string; priority: string; observation: string; occurrence_count: number; is_escalated: boolean; last_observed_at: string | null; }
interface Proposal { id: number; agent_code: string; proposal_type: string; target_type: string; target_key: string; current_behavior: string; proposed_behavior: string; confidence: number; status: string; }
interface Learning { id: number; agent_code: string; category: string; title: string; insight: string; confidence: number; status: string; exception_type: string | null; entity_code: string | null; applied_count: number; created_at: string | null; }
interface Summary { total_reflections: number; escalated_count: number; proposals_by_status: Record<string, number>; }

export default function AgentLearningTab() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [reflections, setReflections] = useState<Reflection[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [learnings, setLearnings] = useState<Learning[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, rRes, pRes, lRes] = await Promise.all([
        authFetch(`${REFLECTIONS_API}/summary`).catch(() => null),
        authFetch(`${REFLECTIONS_API}/?limit=20`).catch(() => null),
        authFetch(`${REFLECTIONS_API}/proposals?limit=20`).catch(() => null),
        authFetch(`${LEARNINGS_API}/?limit=20`).catch(() => null),
      ]);
      if (sRes?.ok) setSummary(await sRes.json());
      if (rRes?.ok) { const d = await rRes.json(); setReflections(d.reflections || d.items || []); }
      if (pRes?.ok) { const d = await pRes.json(); setProposals(d.proposals || d.items || []); }
      if (lRes?.ok) { const d = await lRes.json(); setLearnings(d.learnings || d.items || []); }
    } catch { /* graceful degradation */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleProposal = async (id: number, action: 'approve' | 'reject') => {
    await authFetch(`${REFLECTIONS_API}/proposals/${id}/${action}`, { method: 'POST' });
    load();
  };

  const handleLearning = async (id: number, confirm: boolean) => {
    await authFetch(`${LEARNINGS_API}/${id}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm }),
    });
    load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Loading agent learning data...</div>;

  const pbs = summary?.proposals_by_status || {};
  const confirmedCount = learnings.filter(l => l.status === 'confirmed').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Section A: Summary Stats */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--bg-border)', borderRadius: 8, padding: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Agent Learning Summary</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatCard label="Total Reflections" value={summary?.total_reflections ?? reflections.length} />
          <StatCard label="Escalated Issues" value={summary?.escalated_count ?? 0} color={(summary?.escalated_count ?? 0) > 0 ? '#EF4444' : undefined} />
          <StatCard label="Pending Proposals" value={pbs.proposed ?? 0} color={pbs.proposed ? 'var(--navy-700)' : undefined} />
          <StatCard label="Active Adaptations" value={pbs.active ?? 0} color={pbs.active ? 'var(--accent)' : undefined} />
          <StatCard label="Learnings Extracted" value={learnings.length} />
          <StatCard label="Confirmed Learnings" value={confirmedCount} color={confirmedCount > 0 ? '#22C55E' : undefined} />
        </div>
      </div>

      {/* Section B: Agent Reflections */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--bg-border)', borderRadius: 8, padding: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Agent Reflections</h2>
        {reflections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 12 }}>No reflections recorded yet. Reflections are generated when meta-agents analyze platform performance.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Observation</th>
                <th style={thStyle}>Count</th>
                <th style={thStyle}>Escalated</th>
                <th style={thStyle}>Last Seen</th>
              </tr></thead>
              <tbody>
                {reflections.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <td style={tdStyle}><Badge label={r.priority} color={PRIORITY_COLORS[r.priority] || '#6B7280'} /></td>
                    <td style={tdStyle}>{r.agent_code}</td>
                    <td style={tdStyle}><Badge label={r.reflection_type} color={CATEGORY_COLORS[r.reflection_type] || '#6B7280'} /></td>
                    <td style={{ ...tdStyle, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.observation?.slice(0, 80)}{(r.observation?.length || 0) > 80 ? '...' : ''}</td>
                    <td style={{ ...tdStyle, fontWeight: r.occurrence_count >= 3 ? 700 : 400 }}>{r.occurrence_count}</td>
                    <td style={tdStyle}>{r.is_escalated ? <span style={{ color: '#22C55E' }}>Yes</span> : <span style={{ color: '#6B7280' }}>-</span>}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{timeAgo(r.last_observed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Section C: Adaptation Proposals */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--bg-border)', borderRadius: 8, padding: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Adaptation Proposals</h2>
        {proposals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 12 }}>No adaptation proposals yet. Proposals are generated when agents identify recurring patterns that suggest configuration changes.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Target</th>
                <th style={thStyle}>Current / Proposed</th>
                <th style={thStyle}>Confidence</th>
                <th style={thStyle}>Actions</th>
              </tr></thead>
              <tbody>
                {proposals.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <td style={tdStyle}><Badge label={p.status} color={STATUS_COLORS[p.status] || '#6B7280'} /></td>
                    <td style={tdStyle}>{p.agent_code}</td>
                    <td style={tdStyle}>{p.proposal_type?.replace(/_/g, ' ')}</td>
                    <td style={tdStyle}>{p.target_key || p.target_type}</td>
                    <td style={{ ...tdStyle, maxWidth: 250, fontSize: 11 }}>
                      <span style={{ color: '#EF4444' }}>{(p.current_behavior || '').slice(0, 40)}</span>
                      {' -> '}
                      <span style={{ color: '#22C55E' }}>{(p.proposed_behavior || '').slice(0, 40)}</span>
                    </td>
                    <td style={tdStyle}>{Math.round((p.confidence || 0) * 100)}%</td>
                    <td style={tdStyle}>
                      {p.status === 'proposed' ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => handleProposal(p.id, 'approve')} style={{ padding: '3px 8px', fontSize: 10, background: '#22C55E20', color: '#22C55E', border: '1px solid #22C55E40', borderRadius: 4, cursor: 'pointer' }}>Approve</button>
                          <button onClick={() => handleProposal(p.id, 'reject')} style={{ padding: '3px 8px', fontSize: 10, background: '#EF444420', color: '#EF4444', border: '1px solid #EF444440', borderRadius: 4, cursor: 'pointer' }}>Reject</button>
                        </div>
                      ) : <span style={{ fontSize: 10, color: '#6B7280' }}>{p.status}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Section D: Agent Learnings */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--bg-border)', borderRadius: 8, padding: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Agent Learnings</h2>
        {learnings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 12 }}>No learnings extracted yet. Learnings are automatically extracted when agent conversations are closed.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Insight</th>
                <th style={thStyle}>Confidence</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr></thead>
              <tbody>
                {learnings.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <td style={tdStyle}><Badge label={l.category} color={CATEGORY_COLORS[l.category] || '#6B7280'} /></td>
                    <td style={{ ...tdStyle, fontWeight: 600, maxWidth: 200 }}>{l.title}</td>
                    <td style={{ ...tdStyle, maxWidth: 300, cursor: 'pointer' }} onClick={() => setExpandedInsight(expandedInsight === l.id ? null : l.id)}>
                      {expandedInsight === l.id ? l.insight : (l.insight?.slice(0, 100) + ((l.insight?.length || 0) > 100 ? '...' : ''))}
                    </td>
                    <td style={tdStyle}>{Math.round((l.confidence || 0) * 100)}%</td>
                    <td style={tdStyle}><Badge label={l.status} color={STATUS_COLORS[l.status] || '#6B7280'} /></td>
                    <td style={tdStyle}>
                      {l.status === 'proposed' ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => handleLearning(l.id, true)} style={{ padding: '3px 8px', fontSize: 10, background: '#22C55E20', color: '#22C55E', border: '1px solid #22C55E40', borderRadius: 4, cursor: 'pointer' }}>Confirm</button>
                          <button onClick={() => handleLearning(l.id, false)} style={{ padding: '3px 8px', fontSize: 10, background: '#EF444420', color: '#EF4444', border: '1px solid #EF444440', borderRadius: 4, cursor: 'pointer' }}>Reject</button>
                        </div>
                      ) : <span style={{ fontSize: 10, color: '#6B7280' }}>{l.status}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline StatCard (matches AIGovernance pattern) ──
function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 16px', background: 'rgba(0,0,0,0.03)', borderRadius: 6, border: '1px solid var(--bg-border)', minWidth: 120 }}>
      <div style={{ fontSize: 24, fontWeight: 600, color: color || '#fff' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>{label}</div>
    </div>
  );
}
